import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  GITLAB_PROVISIONING_PAGE_SIZE,
  useGitlabProvisioningGroupsAndProjectsQuery,
} from 'network/queries/useGitlabProvisioningGroupsAndProjectsQuery';
import { useGitlabProvisioningInstallationGroupsAndProjectsQuery } from 'network/queries/useGitlabProvisioningInstallationGroupsAndProjectsQuery';
import { useDebounceValue } from 'usehooks-ts';
import type {
  GitlabProvisioningSelectGroupItem,
  GitlabProvisioningSelectProjectItem,
} from 'network/httpClients/GitlabClient';

const SEARCH_DEBOUNCE_MS = 300;

/** A breadcrumb crumb; the last crumb is the current level. */
export type ScopeBrowserCrumb = {
  id: number;
  name: string;
};

/**
 * The stateless list context; undefined gates the query. Install (pre-install
 * Select) keys off the pasted `gitlabUrl`; Manage keys off the target
 * `installationId` (the server resolves scope from the install row). Either way
 * the elevation PAT rides in the body.
 */
export type ScopeBrowserContext =
  | { kind: 'url'; accessToken: string; gitlabUrl: string }
  | { kind: 'installation'; accessToken: string; installationId: string };

/**
 * Mode-agnostic browsing state for the v2 GitLab scope tree: the merged
 * `groups-and-projects` listing with search (scope-wide flat), lazy per-level
 * drill-down, breadcrumbs, and pagination. Owns nothing about selection — the
 * install and manage containers layer their own selection/coverage policy on top.
 * @param context - PAT + GitLab URL for the stateless list call; undefined disables the query.
 * @returns Browsing state (items, capability, navigation, pagination) for `ScopeBrowserView`.
 */
export const useScopeBrowser = (context: ScopeBrowserContext | undefined) => {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useDebounceValue(
    searchInput,
    SEARCH_DEBOUNCE_MS,
  );
  const searchTerm = debouncedSearch.trim();
  const isSearching = searchTerm.length > 0;
  const [currentPage, setCurrentPage] = useState(1);
  // Deep-paging cursors indexed by page: cursors[0] is undefined (page 1); cursors[N]
  // holds the opaque `nextCursor` returned when page N loaded, i.e. the cursor to
  // request page N+1. Navigation is strict prev/next, so this array fills in order.
  // Falls back to page-based paging when a cursor is absent (older BE), which stays
  // correct because `page` is always sent alongside. Reset alongside `currentPage`.
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const currentCursor = cursors[currentPage - 1];

  const [breadcrumbs, setBreadcrumbs] = useState<ScopeBrowserCrumb[]>([]);
  const currentParentId =
    breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].id : undefined;

  // Loaded group id → fullPath, accumulated across navigation for selection.
  // Held as state (not a ref) so a newly-loaded level's paths reliably trigger a
  // recompute of the derived selection — a ref mutation wouldn't re-render.
  const [groupPathsById, setGroupPathsById] = useState<Map<number, string>>(
    new Map(),
  );
  // Loaded project id → pathWithNamespace, to label results (which carry only ids
  // while a scope is still queued/in_progress).
  const [projectPathsById, setProjectPathsById] = useState<Map<number, string>>(
    new Map(),
  );
  // Loaded group/project id → its owning top-level group id (BE-stamped per item), so a
  // multi-group selection partitions by a real id — correct even for a searched-deep
  // item whose top-level group was never loaded at the root.
  const [topLevelGroupIdByGroupId, setTopLevelGroupIdByGroupId] = useState<
    Map<number, number>
  >(new Map());
  const [topLevelGroupIdByProjectId, setTopLevelGroupIdByProjectId] = useState<
    Map<number, number>
  >(new Map());
  // Top-level group id → whether that group supports group webhooks (Premium+),
  // accumulated from the per-item flag the cloud multi-group listing stamps on every
  // group item. Lets the install container decide group-vs-repo mode per top-level
  // group. Empty on the single-group cloud / self-managed paths, which read the
  // page-level `groupWebhooksSupported` instead.
  const [
    groupWebhooksSupportedByTopLevelGroupId,
    setGroupWebhooksSupportedByTopLevelGroupId,
  ] = useState<Map<number, boolean>>(new Map());

  // Search is scope-wide flat: never scope it to a parent (avoids a transient
  // parent-scoped request during the search-mode transition).
  const activeParentId = isSearching ? undefined : currentParentId;

  // Signature of the current scope (host/install + level + search). A change means
  // the listing must re-page from the start.
  const scopeContextId =
    context?.kind === 'installation'
      ? context.installationId
      : context?.gitlabUrl ?? '';
  const scopeKey = `${context?.kind ?? 'none'}:${scopeContextId}:${
    activeParentId ?? 'root'
  }:${searchTerm}`;
  const prevScopeKeyRef = useRef(scopeKey);
  const scopeChanged = prevScopeKeyRef.current !== scopeKey;
  // The query always uses these, never the raw state: on a scope change they force
  // page 1 / no cursor in the same render, so the request can never carry a cursor
  // minted for the previous scope (`cursor` takes precedence over `page` server-side).
  // The committed reset (layout effect below) then matches these, so the query key is
  // stable and the request goes out exactly once — independent of effect timing.
  const effectivePage = scopeChanged ? 1 : currentPage;
  const effectiveCursor = scopeChanged ? undefined : currentCursor;

  // Both hooks are always called (rules of hooks); only the one matching the
  // context kind is enabled, the other is gated off by an undefined context.
  const urlQuery = useGitlabProvisioningGroupsAndProjectsQuery(
    context?.kind === 'url' ? context : undefined,
    activeParentId,
    searchTerm,
    effectivePage,
    effectiveCursor,
    { enabled: true },
  );
  const installationQuery =
    useGitlabProvisioningInstallationGroupsAndProjectsQuery(
      context?.kind === 'installation' ? context : undefined,
      activeParentId,
      searchTerm,
      effectivePage,
      effectiveCursor,
      { enabled: true },
    );
  const query = context?.kind === 'installation' ? installationQuery : urlQuery;

  // Group-webhook capability rides on the list envelope (per call), not connect.
  // Drives group vs repo mode.
  const groupWebhooksSupported = query.data?.groupWebhooksSupported ?? false;
  const isRepoMode = !groupWebhooksSupported;

  const items = query.data?.items ?? [];
  const groupItems = items.filter(
    (item): item is GitlabProvisioningSelectGroupItem => item.type === 'group',
  );
  const projectItems = items.filter(
    (item): item is GitlabProvisioningSelectProjectItem =>
      item.type === 'project',
  );

  const isLoading = query.isLoading;
  const isError = query.isError;
  const hasItems = items.length > 0;
  const isDrilledIn = currentParentId !== undefined;

  // `!= null` catches both null (the wire value for a broad >100-result search) and
  // undefined, so an unknown total degrades to a bare "page X" (no "of Y"). Paging
  // stays driven by `hasMore` / the cursor, never by totalPages, so a null total does
  // not cap navigation at one page.
  const totalPages =
    query.data?.total != null
      ? Math.max(1, Math.ceil(query.data.total / GITLAB_PROVISIONING_PAGE_SIZE))
      : undefined;

  // Accumulate group paths as levels load, to resolve selection coverage. Copy
  // the map only once a new/changed entry is seen; return the prior map unchanged
  // otherwise so we don't trigger a needless re-render.
  useEffect(() => {
    setGroupPathsById((prev) => {
      let next = prev;

      groupItems.forEach((node) => {
        if (next.get(node.id) === node.fullPath) return;

        if (next === prev) next = new Map(prev);

        next.set(node.id, node.fullPath);
      });

      return next;
    });
  }, [groupItems]);

  useEffect(() => {
    setProjectPathsById((prev) => {
      let next = prev;

      projectItems.forEach((node) => {
        if (next.get(node.id) === node.pathWithNamespace) return;

        if (next === prev) next = new Map(prev);

        next.set(node.id, node.pathWithNamespace);
      });

      return next;
    });
  }, [projectItems]);

  useEffect(() => {
    setTopLevelGroupIdByGroupId((prev) => {
      let next = prev;

      groupItems.forEach((node) => {
        if (node.topLevelGroupId === undefined) return;

        if (next.get(node.id) === node.topLevelGroupId) return;

        if (next === prev) next = new Map(prev);

        next.set(node.id, node.topLevelGroupId);
      });

      return next;
    });
  }, [groupItems]);

  useEffect(() => {
    setTopLevelGroupIdByProjectId((prev) => {
      let next = prev;

      projectItems.forEach((node) => {
        if (node.topLevelGroupId === undefined) return;

        if (next.get(node.id) === node.topLevelGroupId) return;

        if (next === prev) next = new Map(prev);

        next.set(node.id, node.topLevelGroupId);
      });

      return next;
    });
  }, [projectItems]);

  useEffect(() => {
    setGroupWebhooksSupportedByTopLevelGroupId((prev) => {
      let next = prev;

      groupItems.forEach((node) => {
        if (
          node.topLevelGroupId === undefined ||
          node.groupWebhooksSupported === undefined
        )
          return;

        if (next.get(node.topLevelGroupId) === node.groupWebhooksSupported)
          return;

        if (next === prev) next = new Map(prev);

        next.set(node.topLevelGroupId, node.groupWebhooksSupported);
      });

      return next;
    });
  }, [groupItems]);

  // Store this page's `nextCursor` (undefined on an older BE / the last page) so the
  // next page can deep-page instead of re-walking. Only writes on a real response —
  // never on the transient loading `undefined`, which would wipe an already-stored
  // cursor. Writes cursors[currentPage] (the next page's cursor), leaving
  // cursors[currentPage - 1] (this page's) untouched, so the current query key stays
  // stable and there is no refetch loop.
  useEffect(() => {
    const data = query.data;

    if (!data) return;

    setCursors((prev) => {
      if (prev[currentPage] === data.nextCursor) return prev;

      const next = [...prev];

      next[currentPage] = data.nextCursor;

      return next;
    });
  }, [query.data, currentPage]);

  // Commit the page/cursor reset when the scope changes, matching the synchronous
  // `effectivePage`/`effectiveCursor` used in render above. `useLayoutEffect` (not
  // `useEffect`) commits the reset before paint, so the exposed `currentPage` state
  // and the `setCurrentPage` setter are never out of sync in a painted frame — a
  // functional update (`page => page + 1`) always sees the displayed page. Advancing
  // the ref here flips `scopeChanged` back off so the query key stays stable after.
  useLayoutEffect(() => {
    if (prevScopeKeyRef.current === scopeKey) return;

    prevScopeKeyRef.current = scopeKey;
    setCurrentPage(1);
    setCursors([undefined]);
  }, [scopeKey]);

  // Searching is scope-wide flat, so clear the breadcrumb trail.
  useEffect(() => {
    if (isSearching && breadcrumbs.length > 0) setBreadcrumbs([]);
  }, [isSearching, breadcrumbs.length]);

  const onDrillDown = (group: GitlabProvisioningSelectGroupItem) => {
    // Drilling from a flat search result must exit search — the group's children can't
    // load while the flat-search query is active. Flush the debounce so `isSearching`
    // flips off this render (no flicker), and start a fresh trail at the drilled group
    // (its ancestry was never loaded, so "back" returns to the root).
    if (isSearching) {
      setSearchInput('');
      setDebouncedSearch('');
      setDebouncedSearch.flush();
      setBreadcrumbs([{ id: group.id, name: group.name }]);

      return;
    }

    setBreadcrumbs((prev) => [...prev, { id: group.id, name: group.name }]);
  };

  const onNavigateToLevel = (index: number) => {
    setBreadcrumbs((prev) => (index === -1 ? [] : prev.slice(0, index + 1)));
  };

  // Clear all browsing state for a fresh start (e.g. Manage switching PATs), so
  // no navigation/paths from a prior token leak into the next.
  const reset = () => {
    setSearchInput('');
    setCurrentPage(1);
    setCursors([undefined]);
    setBreadcrumbs([]);
    setGroupPathsById(new Map());
    setProjectPathsById(new Map());
    setTopLevelGroupIdByGroupId(new Map());
    setTopLevelGroupIdByProjectId(new Map());
    setGroupWebhooksSupportedByTopLevelGroupId(new Map());
  };

  return {
    searchInput,
    setSearchInput,
    isSearching,
    // Raw state (not `effectivePage`): the layout-effect reset keeps it in sync with
    // the query's page before paint, so the displayed page and the `setCurrentPage`
    // setter never diverge in a painted frame.
    currentPage,
    setCurrentPage,
    breadcrumbs,
    onDrillDown,
    onNavigateToLevel,
    reset,
    items,
    groupItems,
    projectItems,
    groupWebhooksSupported,
    isRepoMode,
    isLoading,
    isError,
    hasItems,
    isDrilledIn,
    totalPages,
    hasMore: query.data?.hasMore ?? false,
    groupPathsById,
    projectPathsById,
    topLevelGroupIdByGroupId,
    topLevelGroupIdByProjectId,
    groupWebhooksSupportedByTopLevelGroupId,
    refetch: query.refetch,
    isFetching: query.isFetching,
    /** The list query error (typed token errors live here for Manage's PAT field). */
    error: query.error,
  };
};

export type ScopeBrowserState = ReturnType<typeof useScopeBrowser>;
